<script>
import { mapState } from "vuex";
import upgradePlanMixin from "@/mixins/upgradePlan";
import trackAndRedirectToUpgrade from "@/mixins/trackAndRedirectToUpgrade";
import { ACCOUNT_LINKS, ACCOUNT_SYNC_TASKS_LINK } from "@/constants";
import UserAvatar from "@/components/global/user_avatar.vue";
import EgDropdown from "@/components/global/eg_dropdown.vue";
import TourCompassButton from "@/components/global/tour_compass_button.vue";
import { ROUTE_CONTEXT_MAP } from "@/init/onboarding/tourContexts.js";
import { openCrispChat } from "@/init/crisp";
import { resetOnboardings } from "@/init/onboarding/onboardings";

export default {
  name: "GlobalNavbar",
  components: {
    UserAvatar,
    EgDropdown,
    TourCompassButton,
  },
  mixins: [upgradePlanMixin, trackAndRedirectToUpgrade],
  props: {
    showOnboardingTour: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      ACCOUNT_LINKS,
      coupon: "20DISC",
    };
  },

  computed: {
    ...mapState(["user", "config"]),

    cid() {
      return this.user?.cid;
    },

    isUserSubscribed() {
      return this.user?.stripeSubscriptionId;
    },

    litePlan() {
      return this.$store.getters.litePlan;
    },

    currentTourContext() {
      return ROUTE_CONTEXT_MAP[this.$route?.name] || null;
    },

    accountDropdownLinks() {
      // TODO: can we use this.features.bulkImports for ToT? Should we also consider profile.provider ("canvasPersonal")?
      const accountLinks = this.config.features?.bulkImports
        ? [...ACCOUNT_LINKS, ACCOUNT_SYNC_TASKS_LINK]
        : ACCOUNT_LINKS;

      if (this.isUserSubscribed) {
        return accountLinks;
      }

      return accountLinks.map((item) =>
        item.path === "/account"
          ? { ...item, path: "/account?tab=subscription" }
          : item
      );
    },
  },

  methods: {
    async onClickResetOnboardings() {
      await resetOnboardings();
      this.$eventBus.$emit("onboarding:reset");
    },

    logout() {
      this.$getClerk()?.signOut();
      this.$store.dispatch("logout");
    },

    openSupportChat() {
      openCrispChat();
    },
  },
};
</script>

<template>
  <div class="navbar flex-shrink-0">
    <div class="navbar-content d-flex align-items-center w-100">
      <div class="navbar-right">
        <tour-compass-button
          v-if="currentTourContext"
          :context="currentTourContext"
        />
        <eg-dropdown
          right
          no-caret
          variant="none"
          toggle-class="p-0 border-0 ml-3"
        >
          <template #button-content>
            <eg-button
              type="ordinary"
              size="small"
              :title="$t('common.userMenu')"
              class="avatar-button"
            >
              <template #icon>
                <user-avatar v-if="user" :user="user" />
              </template>
            </eg-button>
          </template>

          <eg-b-dropdown-text>
            <div class="top-content">
              <div class="text-truncate paragraph-s">
                {{ user?.firstName }} {{ user?.lastName }}
              </div>

              <div class="caption-s text-truncate color-text-neutral-secondary">
                {{ user?.email }}
              </div>
            </div>
          </eg-b-dropdown-text>

          <eg-b-dropdown-divider />

          <eg-b-dropdown-item
            v-for="link in accountDropdownLinks"
            :key="link.translationKey"
            :to="link.path"
          >
            <eg-icon :icon="link.icon" size="small" />
            {{ $t(link.translationKey) }}
          </eg-b-dropdown-item>

          <eg-b-dropdown-divider />

          <eg-b-dropdown-item @click="logout">
            <eg-icon icon="exit-logout" class="text-danger" size="small" />
            <div class="color-text-semantic-on-error">
              {{ $t("common.logout") }}
            </div>
          </eg-b-dropdown-item>
        </eg-dropdown>
      </div>
    </div>
  </div>
</template>

<style lang="css" scoped>
.navbar {
  position: sticky;
  top: 0;
  z-index: 6;
  height: var(--navbar-height);
  background-color: white;
  border-bottom: 1px solid var(--color-stroke-neutral-default);
  display: flex;
  align-items: center;
  padding: 0 20px;
  width: 100%;
}

.navbar-content {
  max-height: 100%;
}

.navbar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.navbar-brand {
  font-size: 1rem;
}

.trial-item .nav-link {
  padding: 1.5px;
}

.avatar-button:active,
.avatar-button.eg-new-button--ordinary:active:not(:disabled) {
  background-color: transparent;
}
</style>

<style lang="scss">
:root {
  --navbar-height: 48px;
  --main-header-height: var(--navbar-height);
}

.navbar .dropdown .btn {
  background: none;
  border: none;

  &::after {
    display: none;
  }

  &:hover {
    background: none;
  }
}
</style>
